import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { GALLERY } from "../data/gallery";

const FILTERS = [
  { key: "all", label: "ALL FRAMES" },
  { key: "wtac", label: "WTAC" },
  { key: "unmarked", label: "UNMARKED" },
  { key: "shoots", label: "CAR SHOOTS" },
];

const CAT_LABELS = {
  wtac: "WORLD TIME ATTACK CHALLENGE",
  unmarked: "UNMARKED",
  shoots: "CAR SHOOTS",
};

function Lightbox({ items, index, onClose, onNav }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onNav]);

  const item = items[index];

  return (
    <motion.div
      data-testid="gallery-lightbox"
      className="fixed inset-0 z-[95] bg-ink/95 backdrop-blur-md flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close-button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-6 right-6 z-10 w-12 h-12 flex items-center justify-center border border-white/25 text-white hover:border-signal hover:text-signal transition-colors bg-ink/60"
      >
        <X size={20} />
      </button>
      <button
        data-testid="lightbox-prev-button"
        onClick={(e) => { e.stopPropagation(); onNav(-1); }}
        aria-label="Previous photo"
        className="absolute left-4 md:left-8 z-10 w-12 h-12 flex items-center justify-center border border-white/25 text-white hover:border-signal hover:text-signal transition-colors bg-ink/60"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        data-testid="lightbox-next-button"
        onClick={(e) => { e.stopPropagation(); onNav(1); }}
        aria-label="Next photo"
        className="absolute right-4 md:right-8 z-10 w-12 h-12 flex items-center justify-center border border-white/25 text-white hover:border-signal hover:text-signal transition-colors bg-ink/60"
      >
        <ChevronRight size={22} />
      </button>

      <motion.div
        className="max-w-[90vw] md:max-w-[80vw]"
        key={index}
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          data-testid="lightbox-image"
          src={item.full}
          alt={`${CAT_LABELS[item.category]} — frame ${index + 1}`}
          className="max-h-[80vh] max-w-full object-contain letterbox"
        />
        <div className="flex justify-between mt-4 font-mono text-[11px] tracking-[0.3em] text-smoke">
          <span>{CAT_LABELS[item.category]}</span>
          <span data-testid="lightbox-counter">
            {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function GalleryPage() {
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    document.title = "Gallery — Pursuit Media Studios";
  }, []);

  const items = filter === "all" ? GALLERY : GALLERY.filter((g) => g.category === filter);

  const pickFilter = (key) => {
    setFilter(key);
    setOpen(null);
  };

  const nav = (dir) =>
    setOpen((i) => (i === null ? null : (i + dir + items.length) % items.length));

  return (
    <section
      id="gallery"
      data-testid="gallery-page"
      className="relative bg-ink min-h-screen pt-32 pb-24 md:pb-36"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono text-[10px] tracking-[0.4em] text-signal mb-4">GALLERY — 2026 SEASON</p>
          <h1
            className="font-display text-5xl md:text-8xl text-white leading-[0.95] mb-6"
            style={{ fontWeight: 900 }}
          >
            STILL <span className="text-outline">FRAMES</span>
          </h1>
          <p className="text-smoke text-base font-light max-w-xl mb-10">
            Every frame from every session — filter by shoot and click any image to view it
            full-screen.
          </p>
        </Reveal>

        <div className="flex flex-wrap items-center gap-3 mb-10" data-testid="gallery-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => pickFilter(f.key)}
              className={`font-mono text-[11px] tracking-[0.25em] px-5 py-2.5 border transition-all duration-300 ${
                filter === f.key
                  ? "bg-signal border-signal text-white"
                  : "border-white/20 text-white/70 hover:border-signal/70 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="ml-auto font-mono text-[11px] tracking-[0.3em] text-smoke" data-testid="gallery-count">
            {String(items.length).padStart(2, "0")} FRAMES
            {filter !== "all" ? ` — ${CAT_LABELS[filter]}` : ""}
          </span>
        </div>

        <motion.div
          key={filter}
          className="columns-2 md:columns-3 xl:columns-4 gap-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          {items.map((item, i) => (
            <Reveal key={item.full} delay={(i % 4) * 0.04} className="mb-3 break-inside-avoid">
              <button
                data-testid={`gallery-item-${i + 1}`}
                onClick={() => setOpen(i)}
                className="group relative block w-full overflow-hidden letterbox rounded-sm cursor-pointer"
              >
                <img
                  src={item.thumb}
                  alt={`${CAT_LABELS[item.category]} — frame ${i + 1}`}
                  loading="lazy"
                  className="w-full transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
                />
                <span className="absolute inset-0 bg-signal/0 group-hover:bg-signal/15 transition-colors duration-300" />
                <span className="absolute bottom-2 left-2 font-mono text-[9px] tracking-[0.25em] text-white/0 group-hover:text-white/90 transition-colors duration-300 bg-ink/60 backdrop-blur-sm px-2 py-1">
                  {CAT_LABELS[item.category]}
                </span>
              </button>
            </Reveal>
          ))}
        </motion.div>
      </div>

      <AnimatePresence>
        {open !== null && (
          <Lightbox items={items} index={open} onClose={() => setOpen(null)} onNav={nav} />
        )}
      </AnimatePresence>
    </section>
  );
}
